import React from 'react'
import { RenderWhenVisible, useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreative } from '@marketing-milk/interfaces'
import { prop } from 'lodash/fp'
import { isValidCreative } from 'pages/business/campaigns/helpers/campaign.helpers'
import { useEffect, useRef, useState } from 'react'
import { CreativeCard } from '../../CreativeCard/CreativeCard'
import { useCreatives } from '../../useCreatives'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'
import { sortByUpdatedAt } from '../../helpers/creative.helpers'

type Props = {
  selectedCreatives: AdCreative[]
  onSelectCreative: (creative: AdCreative) => void
}
export const BulkCreativeSelect = (props: Props) => {
  const businessId = useGetBusinessID()
  const { creatives, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useCreatives(
    String(businessId),
    { complete: true }
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const isSelected = (creative: AdCreative) =>
    props.selectedCreatives.map(prop('id')).includes(creative.id)

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || !containerRef.current) return
    const scrollParent =
      containerRef.current.closest<HTMLElement>('.overflow-y-auto') ?? document.documentElement
    const onScroll = () => {
      if (isFetchingNextPage) return
      if (scrollParent.scrollTop + scrollParent.clientHeight > scrollParent.scrollHeight - 200) {
        fetchNextPage()
      }
    }
    scrollParent.addEventListener('scroll', onScroll)
    return () => scrollParent.removeEventListener('scroll', onScroll)
  }, [hasNextPage, fetchNextPage, isFetchingNextPage])

  const filtered = creatives?.filter(isValidCreative)

  return (
    <div ref={containerRef}>
      <div className="flex w-full space-x-4 p-4 pb-2 items-center justify-center">
        <input
          className="bg-white w-full rounded-md border px-4 py-3 max-w-6xl"
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Filter Creatives"
        />
      </div>

      {filtered && filtered.length === 0 && !isFetching && (
        <div className="text-center text-gray-500 py-8">No creatives found.</div>
      )}

      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(20rem,1fr))] gap-5">
        {fuzzySearch(sortByUpdatedAt(filtered ?? []), searchTerm).map(creative => (
          <RenderWhenVisible>
            <CreativeCard
              tools={false}
              creative={creative}
              key={creative.id}
              onSelect={props.onSelectCreative}
              selectedText={isSelected(creative) ? 'Selected' : undefined}
            />
          </RenderWhenVisible>
        ))}
      </div>
    </div>
  )
}
